/**
 * ANIMATION-MODE
 * Coupe les animations des graphiques quand l'écran est photographié (export image, rapport PowerPoint)
 * ou quand l'utilisateur a demandé de réduire les animations dans son système.
 *
 * Les exports capturent l'écran tel qu'il est à cet instant : sans ce mode ils figent des compteurs à mi-course,
 * des barres à moitié dessinées ou des cartes encore invisibles (apparition au défilement).
 */
import { MotionGlobalConfig } from "framer-motion"
import { useSyncExternalStore } from "react"

//Durée maximale des animations du dashboard (compteur 900 ms + décalages) : laisse finir celles déjà lancées
export const ANIMATIONS_SETTLE_MS = 1300

//Compteur plutôt que booléen : un export peut être lancé pendant la génération d'un rapport
let exportDepth = 0
let isReducedMotion = false
const listeners = new Set<() => void>()

function areAnimationsDisabled(): boolean {
  return exportDepth > 0 || isReducedMotion
}

function sync() {
  //Les animations framer-motion lancées à partir de maintenant sautent directement à leur valeur finale
  MotionGlobalConfig.skipAnimations = areAnimationsDisabled()
  listeners.forEach((listener) => listener())
}

//Préférence « réduire les animations » du système, suivie en direct
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  const query = window.matchMedia("(prefers-reduced-motion: reduce)")
  isReducedMotion = query.matches
  query.addEventListener?.("change", () => {
    isReducedMotion = query.matches
    sync()
  })
  sync()
}

//Entrer / sortir du mode export (toujours appairés : try ... finally)
export function enterExportMode() {
  exportDepth += 1
  sync()
}

export function leaveExportMode() {
  exportDepth = Math.max(0, exportDepth - 1)
  sync()
}

//Entre en mode export puis attend que les animations déjà lancées se terminent
export async function enterExportModeAndSettle() {
  enterExportMode()
  await new Promise((resolve) => setTimeout(resolve, ANIMATIONS_SETTLE_MS))
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

//true pendant un export ou si l'utilisateur a demandé de réduire les animations
export function useAnimationsDisabled(): boolean {
  return useSyncExternalStore(subscribe, areAnimationsDisabled, () => false)
}
